import * as React from "react";
import * as ReactDOM from "react-dom";
import ChatMessage from "./ChatMessage";
import AgentsTyping from "./AgentsTyping";
import ProgressBar from "./ProgressBar";
import {
  Message,
  QuickReplyButton,
  DataField,
  SecureForm,
  ChatUIStage
} from "../types";
import StoreContainer from "../stores/StoreContainer";
import ChatQuickReplies from "./ChatQuickReplies";
import KnowledgeBaseMessage from "./KnowledgeBaseMessage";
import SecureFormDisplay from "./SecureFormDisplay";
import SearchSpringView from "./SearchSpringView";

interface ChatMessageListProps {
  chatStage: ChatUIStage;
  messages: Array<Message>;
  agentsTyping: Array<string>;
  uploadPercentage: number;
  showUploadProgress: boolean;
  quickReplies: QuickReplyButton[];
  dataField: DataField;
  secureForm: SecureForm;
  onClickExit(): void;
  onSetLineRating(lineId: number, rating: number): void;
  onResendMessage(message: Message): void;
  onClickQuickReply: (button: QuickReplyButton) => void;
  onClickActionCard: (name: string, data: string) => void;
  onSubmitDataField: (value: string) => void;
}

/**
 * Any changes to this component or its children should be accompanied by
 * a `yarn build` and testing the Engagement Design screen in agent console.
 * Keep this component as dumb as possible so it can be easily reused.
 */

class ChatMessageList extends React.Component<ChatMessageListProps, {}> {
  wasScrolledToBottom = true;
  el: HTMLDivElement;

  componentDidMount() {
    this.scrollToBottom();
  }

  componentDidUpdate(prevProps: ChatMessageListProps) {
    const newMessageReceived =
      this.props.messages.length > prevProps.messages.length;
    const uploadStarted =
      this.props.showUploadProgress !== prevProps.showUploadProgress;

    if (newMessageReceived && this.wasScrolledToBottom) {
      this.scrollToBottom();
    }

    if (uploadStarted && this.wasScrolledToBottom) {
      this.scrollToBottom();
    }

    if (this.props.agentsTyping && this.wasScrolledToBottom) {
      this.scrollToBottom();
    }
  }

  getQuickReplies() {
    const messages = this.props.messages;

    if (!messages || !messages.length) {
      return null;
    }

    const quickReplies = this.props.quickReplies;
    return quickReplies && quickReplies.slice();
  }

  getKnowledgeBaseSearchResults() {
    return StoreContainer.BotStore.getKnowledgeBaseSearchResults();
  }

  getCalendlyResult() {
    return StoreContainer.BotStore.getCalendlyResult();
  }

  getSearchSpringResults() {
    return StoreContainer.BotStore.getSearchSpringResults();
  }

  onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const containerHeight =
      e.currentTarget.scrollHeight - e.currentTarget.offsetHeight;
    const delta = containerHeight - e.currentTarget.scrollTop;

    // we'll leave a little buffer here so if they were near the bottom,
    // they'll still be scrolled down.

    this.wasScrolledToBottom = delta < 200;
  };

  scrollToBottom() {
    const node = ReactDOM.findDOMNode(this) as any;
    node.scrollTop = 999999;
  }

  renderUploadProgress() {
    if (!this.props.showUploadProgress) {
      return null;
    }

    return (
      <div style={{ marginTop: 16 }}>
        <ProgressBar percentage={this.props.uploadPercentage} />
      </div>
    );
  }

  renderMessages() {
    const messages = this.props.messages.map((message) => {
      return {
        ...message,
        timestamp: this.removeSeconds(message.timestamp)
      };
    });
    return messages.map((message) =>
      this.renderMessage(message, messages[messages.indexOf(message) - 1])
    );
  }

  removeSeconds(timestamp: Date): Date {
    //Clear the seconds and miliseconds off of the timestamp - we don't care about seconds here
    const newDate = new Date(timestamp.getTime());
    newDate.setSeconds(0, 0);
    return newDate;
  }

  renderMessage(message: Message, previousMessage: Message) {
    //Only show timestamp for this message if the minute has changed or the author has changed from the previous message
    const skipTimestamp =
      previousMessage &&
      message.timestamp.valueOf() === previousMessage.timestamp.valueOf() &&
      previousMessage.author === message.author;

    const disableTimestamp =
      this.props.chatStage === ChatUIStage.AssignedToBot &&
      !StoreContainer.BotStore.botTimestampsEnabled();

    return (
      <ChatMessage
        key={message.id}
        message={message}
        skipTimestamp={skipTimestamp || disableTimestamp}
        onHeightChange={() => this.scrollToBottom()}
        onClickExit={this.props.onClickExit}
        onSetLineRating={this.props.onSetLineRating}
        onResend={() => this.props.onResendMessage(message)}
        onClickActionCard={this.props.onClickActionCard}
      />
    );
  }

  focus() {
    this.el.focus();
  }

  renderQuickReplies() {
    const quickReplyButtons = this.getQuickReplies();
    if (quickReplyButtons) {
      return (
        <ChatQuickReplies
          onClick={this.props.onClickQuickReply}
          quickReplies={quickReplyButtons}
        />
      );
    }

    const dataField = this.props.dataField;
    if (!dataField || dataField.format !== "Buttons") {
      return null;
    }

    const dataFieldButtons = dataField.options.map((option) => ({
      label: option,
      pathId: null
    }));
    if (dataFieldButtons) {
      return (
        <ChatQuickReplies
          onClick={(button) => this.props.onSubmitDataField(button.label)}
          quickReplies={dataFieldButtons}
        />
      );
    }
  }

  render() {
    const kbSearchResults = this.getKnowledgeBaseSearchResults();
    const searchSpringResults = this.getSearchSpringResults();

    return (
      <div
        id="velaro-message-list"
        className="velaro-message-list velaro-scroll"
        onScroll={this.onScroll}
        aria-live="assertive"
        role="log"
        ref={(el) => (this.el = el)}
      >
        <div className="velaro-message-list-inner">
          <div className="velaro-message-list-padding" role="list">
            {this.renderMessages()}
            {this.renderQuickReplies()}
            <SecureFormDisplay
              secureForm={this.props.secureForm}
              agentDisplayName={StoreContainer.ChatStore.getAgentName()}
            />
            {kbSearchResults && (
              <KnowledgeBaseMessage
                searchResults={kbSearchResults}
                onClickQuickReply={this.props.onClickQuickReply}
              />
            )}
            {searchSpringResults && (
              <SearchSpringView data={searchSpringResults} />
            )}
            <AgentsTyping agentsTyping={this.props.agentsTyping} />
            {this.renderUploadProgress()}
          </div>
        </div>
      </div>
    );
  }
}

export default ChatMessageList;
